import type { BillingEntitlementKey } from '@tov-plus/domain';
import {
  assertCapabilityPackagingDefinitions,
  capabilityGroupDefinitions,
  capabilityPackageDefinitions,
} from './capabilityPackaging';

export type SharedCapabilityKey =
  | 'localization'
  | 'collaboration'
  | 'publishing';

export type ProductCapabilityKey =
  | 'multi_language'
  | 'publishing'
  | 'api_access'
  | 'webhooks'
  | 'vendor_access'
  | 'semantic_search'
  | 'hybrid_search'
  | 'recommendation_search'
  | 'intelligence_retrieval'
  | 'advanced_assistant_search';

export type ProductCapabilitySource =
  | 'default'
  | 'package'
  | 'billing_entitlement'
  | 'manual_override'
  | 'internal_flag';

export type CapabilityAssignmentScopeType =
  | 'platform'
  | 'billing_account'
  | 'occasion'
  | 'actor';

export interface ProductCapabilityDefinition {
  key: ProductCapabilityKey;
  displayName: string;
  description: string;
  category: 'experience';
  defaultEnabled: boolean;
  billingEntitlementKey: BillingEntitlementKey | null;
}

export interface ProductCapabilityGrant {
  capabilityKey: ProductCapabilityKey;
  source: ProductCapabilitySource;
  enabled: boolean;
  sourceKey: string | null;
  scopeType: CapabilityAssignmentScopeType;
  scopeId: string | null;
  metadata?: Record<string, unknown>;
}

export interface EffectiveProductCapabilityState {
  key: ProductCapabilityKey;
  enabled: boolean;
  source: ProductCapabilitySource | null;
  sourceKey: string | null;
  reason:
    | 'default_enabled'
    | 'package_assignment'
    | 'billing_entitlement'
    | 'manual_override'
    | 'internal_flag'
    | 'not_enabled';
}

export interface CapabilityResolutionExplanation {
  key: ProductCapabilityKey;
  enabled: boolean;
  source: ProductCapabilitySource | null;
  sourceKey: string | null;
  overriddenBy: {
    source: ProductCapabilitySource;
    sourceKey: string | null;
  } | null;
  reasoning: string;
  evaluatedGrants: ProductCapabilityGrant[];
}

export interface ProductCapabilityResolution {
  states: Record<ProductCapabilityKey, EffectiveProductCapabilityState>;
  explanations: Record<ProductCapabilityKey, CapabilityResolutionExplanation>;
  grants: Record<ProductCapabilityKey, ProductCapabilityGrant[]>;
}

export interface CapabilityResolutionAnomaly {
  code:
    | 'missing_definition'
    | 'conflicting_sources'
    | 'stale_data'
    | 'unknown_scope';
  severity: 'warning' | 'critical';
  capabilityKey: ProductCapabilityKey | null;
  message: string;
  metadata: Record<string, unknown>;
}

export const productCapabilityDefinitions: ProductCapabilityDefinition[] = [
  {
    key: 'multi_language',
    displayName: 'Multi-language',
    description:
      'Enables multilingual authoring and locale-aware guest rendering beyond the single default locale.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.multi_language',
  },
  {
    key: 'publishing',
    displayName: 'Publishing',
    description:
      'Enables planner-facing publishing and export-ready artifact workflows such as invitations, QR cards, signage, and future seating or schedule print outputs.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.advanced_publishing',
  },
  {
    key: 'api_access',
    displayName: 'API access',
    description:
      'Enables tenant-facing external API and integration configuration flows for an occasion.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.integrations',
  },
  {
    key: 'webhooks',
    displayName: 'Webhooks',
    description:
      'Enables occasion-scoped outbound webhook subscription management for external consumers.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.integrations',
  },
  {
    key: 'vendor_access',
    displayName: 'Vendor access',
    description:
      'Enables external vendor participation flows such as vendor portal access, scoped vendor workflows, and planner-side vendor coordination surfaces.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.multi_user_support',
  },
  {
    key: 'semantic_search',
    displayName: 'Semantic search',
    description:
      'Enables embedding-aware semantic retrieval, intent detection, and natural-language search understanding for an occasion.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.semantic_search',
  },
  {
    key: 'hybrid_search',
    displayName: 'Hybrid search',
    description:
      'Enables blended keyword and semantic ranking, retrieval explainability, and advanced search relevance improvements.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.hybrid_search',
  },
  {
    key: 'recommendation_search',
    displayName: 'Recommendation search',
    description:
      'Enables permission-aware related content, suggested searches, and recommendation retrieval infrastructure.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.recommendation_search',
  },
  {
    key: 'intelligence_retrieval',
    displayName: 'Intelligence retrieval',
    description:
      'Enables grounded retrieval contracts for assistants, search context windows, and source-citation-ready search responses.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.intelligence_retrieval',
  },
  {
    key: 'advanced_assistant_search',
    displayName: 'Advanced assistant search',
    description:
      'Enables assistant-oriented hybrid retrieval orchestration, advanced retrieval metadata, and future memory-aware grounding behavior.',
    category: 'experience',
    defaultEnabled: false,
    billingEntitlementKey: 'feature.advanced_assistant_search',
  },
];

export const productCapabilityKeys = productCapabilityDefinitions.map(
  (definition) => definition.key,
);

assertCapabilityPackagingDefinitions(
  capabilityGroupDefinitions,
  capabilityPackageDefinitions,
  productCapabilityKeys,
);

export function resolveProductCapabilityStates(input: {
  definitions?: ProductCapabilityDefinition[];
  grants: ProductCapabilityGrant[];
}) {
  return resolveCapabilities(input).states;
}

const sourcePrecedence: Record<ProductCapabilitySource, number> = {
  manual_override: 5,
  billing_entitlement: 4,
  package: 3,
  internal_flag: 2,
  default: 1,
};

const scopeSpecificity: Record<CapabilityAssignmentScopeType, number> = {
  actor: 4,
  occasion: 3,
  billing_account: 2,
  platform: 1,
};

function createDefaultGrant(
  definition: ProductCapabilityDefinition,
): ProductCapabilityGrant {
  return {
    capabilityKey: definition.key,
    source: 'default',
    enabled: definition.defaultEnabled,
    sourceKey: definition.billingEntitlementKey,
    scopeType: 'platform',
    scopeId: null,
    metadata: {
      description: definition.description,
    },
  };
}

function compareGrants(
  left: { grant: ProductCapabilityGrant; index: number },
  right: { grant: ProductCapabilityGrant; index: number },
) {
  const precedenceDelta =
    sourcePrecedence[right.grant.source] - sourcePrecedence[left.grant.source];

  if (precedenceDelta !== 0) {
    return precedenceDelta;
  }

  const scopeDelta =
    scopeSpecificity[right.grant.scopeType] -
    scopeSpecificity[left.grant.scopeType];

  if (scopeDelta !== 0) {
    return scopeDelta;
  }

  return right.index - left.index;
}

function deriveReason(
  grant: ProductCapabilityGrant | null,
  enabled: boolean,
): EffectiveProductCapabilityState['reason'] {
  if (!grant) {
    return 'not_enabled';
  }

  if (grant.source === 'default') {
    return enabled ? 'default_enabled' : 'not_enabled';
  }

  if (grant.source === 'package') {
    return enabled ? 'package_assignment' : 'not_enabled';
  }

  if (grant.source === 'billing_entitlement') {
    return enabled ? 'billing_entitlement' : 'not_enabled';
  }

  if (grant.source === 'manual_override') {
    return enabled ? 'manual_override' : 'not_enabled';
  }

  if (grant.source === 'internal_flag') {
    return enabled ? 'internal_flag' : 'not_enabled';
  }

  return 'not_enabled';
}

function buildReasoning(
  definition: ProductCapabilityDefinition,
  winner: ProductCapabilityGrant | null,
  competing: ProductCapabilityGrant[],
) {
  if (!winner) {
    return `${definition.displayName} is disabled because no grant enabled it and the default baseline is off.`;
  }

  const base =
    winner.source === 'default'
      ? `${definition.displayName} follows the system default baseline.`
      : `${definition.displayName} is controlled by ${winner.source.replaceAll('_', ' ')}.`;

  if (competing.length === 0) {
    return base;
  }

  return `${base} Higher-precedence resolution suppressed ${competing
    .map((grant) => grant.source.replaceAll('_', ' '))
    .join(', ')} inputs.`;
}

export function resolveCapabilities(input: {
  definitions?: ProductCapabilityDefinition[];
  grants: ProductCapabilityGrant[];
}) {
  const definitions = input.definitions ?? productCapabilityDefinitions;
  const states = {} as Record<
    ProductCapabilityKey,
    EffectiveProductCapabilityState
  >;
  const explanations = {} as Record<
    ProductCapabilityKey,
    CapabilityResolutionExplanation
  >;
  const grants = {} as Record<ProductCapabilityKey, ProductCapabilityGrant[]>;

  for (const definition of definitions) {
    const allGrants = [
      createDefaultGrant(definition),
      ...input.grants.filter((grant) => grant.capabilityKey === definition.key),
    ];
    const sorted = allGrants
      .map((grant, index) => ({
        grant,
        index,
      }))
      .sort(compareGrants)
      .map((entry) => entry.grant);
    const winner = sorted[0] ?? null;
    const enabled = winner?.enabled ?? false;
    const source =
      winner?.source === 'default' && winner.enabled === false
        ? null
        : (winner?.source ?? null);
    const sourceKey =
      winner?.source === 'default' && winner.enabled === false
        ? definition.billingEntitlementKey
        : (winner?.sourceKey ?? definition.billingEntitlementKey);
    const competing = sorted.slice(1);

    states[definition.key] = {
      key: definition.key,
      enabled,
      source,
      sourceKey,
      reason: deriveReason(winner, enabled),
    };
    explanations[definition.key] = {
      key: definition.key,
      enabled,
      source,
      sourceKey,
      overriddenBy: null,
      reasoning: buildReasoning(definition, winner, competing),
      evaluatedGrants: sorted,
    };
    grants[definition.key] = sorted;
  }

  return {
    states,
    explanations,
    grants,
  } satisfies ProductCapabilityResolution;
}

export function getProductCapabilityState(
  states: Record<ProductCapabilityKey, EffectiveProductCapabilityState>,
  capabilityKey: ProductCapabilityKey,
) {
  return states[capabilityKey];
}

export function hasProductCapability(
  states: Record<ProductCapabilityKey, EffectiveProductCapabilityState>,
  capabilityKey: ProductCapabilityKey,
) {
  return states[capabilityKey]?.enabled ?? false;
}

export function explainProductCapability(
  resolution: ProductCapabilityResolution,
  capabilityKey: ProductCapabilityKey,
) {
  return resolution.explanations[capabilityKey];
}

export interface PublishingRequest {
  templateKey: string;
  targetResourceType: string;
  targetResourceId: string;
}

export interface LocalizationSelection {
  localeCode: string;
  fallbackLocaleCode?: string;
}
